import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseAuth } from "@/hooks/useSupabaseAuth";
import { useTenantContext } from "@/context/TenantContext";
import type { FeedbackErrorContext } from "@/context/FeedbackContext";
import type { ConsoleLogEntry } from "@/lib/consoleCapture";
import type { Json } from "@/integrations/supabase/types";

export type FeedbackType = "bug" | "feature" | "question" | "other";

export interface SubmitFeedbackInput {
  type: FeedbackType;
  subject?: string;
  description: string;
  error?: FeedbackErrorContext;
  consoleLogs?: ConsoleLogEntry[];
  screenshot?: Blob | null;
  attachments?: File[];
}

// Defense-in-depth allowlist mirrored from FeedbackForm. The UI validates first; this
// re-validates at submit time so a bad caller cannot bypass the per-file constraints.
const MAX_ATTACHMENT_COUNT = 8;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 40 * 1024 * 1024;
const ALLOWED_ATTACHMENT_MIME_PREFIXES = ["image/", "video/", "audio/", "text/"];
const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
  "application/pdf",
  "application/json",
  "application/zip",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
]);
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  "pdf", "txt", "md", "csv", "json", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "zip",
  "jpg", "jpeg", "png", "gif", "webp", "mp4", "mov", "webm", "mp3", "wav",
]);

function isAllowedAttachmentFile(file: File): boolean {
  if (ALLOWED_ATTACHMENT_MIME_PREFIXES.some((p) => file.type.startsWith(p))) return true;
  if (ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)) return true;
  const extension = file.name.split(".").pop()?.toLowerCase();
  return !!extension && ALLOWED_ATTACHMENT_EXTENSIONS.has(extension);
}

export function useSubmitFeedback() {
  const queryClient = useQueryClient();
  const { user } = useSupabaseAuth();
  const { tenant } = useTenantContext();

  return useMutation({
    mutationFn: async (input: SubmitFeedbackInput) => {
      if (!user?.id) {
        throw new Error("You must be logged in to submit feedback");
      }
      if (!tenant?.id) {
        throw new Error("Tenant context not available");
      }

      const feedbackId = crypto.randomUUID();

      let screenshotPath: string | null = null;
      const attachmentPaths: string[] = [];

      if (input.screenshot) {
        const filename = `screenshot_${Date.now()}.jpg`;
        // Path convention matches storage RLS policy:
        // feedback-screenshots/{tenant_id}/{user_id}/{feedback_id}/{filename}
        screenshotPath = `feedback-screenshots/${tenant.id}/${user.id}/${feedbackId}/${filename}`;

        const { error: uploadError } = await supabase.storage
          .from("feedback-screenshots")
          .upload(screenshotPath, input.screenshot, {
            cacheControl: "3600",
            upsert: false,
            contentType: "image/jpeg",
          });

        if (uploadError) {
          console.error("[useSubmitFeedback] Screenshot upload error:", uploadError);
          // Don’t block the report; fallback to no screenshot.
          screenshotPath = null;
        } else {
          attachmentPaths.push(screenshotPath);
        }
      }

      if (input.attachments?.length) {
        // Defense in depth: re-validate caller's files even if the UI already filtered.
        const candidates = input.attachments
          .filter((f) => f && f.size > 0 && f.size <= MAX_ATTACHMENT_BYTES)
          .filter(isAllowedAttachmentFile)
          .slice(0, MAX_ATTACHMENT_COUNT);

        let totalBytes = 0;
        const toUpload: File[] = [];
        for (const file of candidates) {
          if (totalBytes + file.size > MAX_TOTAL_ATTACHMENT_BYTES) break;
          toUpload.push(file);
          totalBytes += file.size;
        }

        for (let i = 0; i < toUpload.length; i++) {
          const file = toUpload[i];
          const safeName = (file.name || `attachment_${i + 1}`).replace(/[^a-zA-Z0-9._-]/g, "_");
          const filename = `attachment_${i + 1}_${Date.now()}_${safeName}`;
          const path = `feedback-screenshots/${tenant.id}/${user.id}/${feedbackId}/${filename}`;

          const { error: uploadError } = await supabase.storage
            .from("feedback-screenshots")
            .upload(path, file, {
              cacheControl: "3600",
              upsert: false,
              contentType: file.type || "application/octet-stream",
            });

          if (uploadError) {
            console.error("[useSubmitFeedback] Attachment upload error:", uploadError);
            continue;
          }
          attachmentPaths.push(path);
        }
      }

      const { data, error } = await supabase
        .from("feedback_reports")
        .insert({
          id: feedbackId,
          tenant_id: tenant.id,
          user_id: user.id,
          type: input.type,
          subject: input.subject ?? null,
          description: input.description,
          error_message: input.error?.message ?? null,
          error_stack: input.error?.stack ?? null,
          console_logs: input.consoleLogs
            ? (JSON.parse(JSON.stringify(input.consoleLogs)) as Json)
            : null,
          url: window.location.href,
          user_agent: navigator.userAgent,
          browser_info: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
          } as Json,
          screenshot_url: screenshotPath,
          attachment_urls: attachmentPaths.length ? attachmentPaths : null,
        })
        .select()
        .single();

      if (error) {
        console.error("[useSubmitFeedback] Insert error:", error);
        throw error;
      }

      // Fire-and-forget email notification (edge function will verify JWT)
      try {
        await supabase.functions.invoke("send-feedback-notification", {
          body: { feedback_id: data.id },
        });
      } catch (notifyErr) {
        console.warn("[useSubmitFeedback] Notification send failed (non-blocking):", notifyErr);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feedback_reports", tenant?.id] });
    },
  });
}
